import React, { PureComponent } from "react";
import type { ApolloClient } from "@apollo/client";
import { connect, type ConnectedProps } from "react-redux";
import { Col, Form, Row } from "react-bootstrap";
import {
  chain,
  filter,
  find,
  get,
  isEmpty,
  map,
  size,
  some,
  toLower,
} from "lodash";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { clearUploadedDocuments } from "actions/userActions";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { adminConstants } from "actions/types";
import { toast } from "react-toastify";
import {
  getKybDocumentTypes,
  requestMemberFieldEdit,
} from "services/adminService";
import {
  getKybStatusAndInfo,
  submitBusinessDocuments,
  submitBusinessMemberDocuments,
  submitBusinessRegistrationUpdate,
} from "actions/adminActions";

import moment from "moment";
import FileUploader from "components/FileUploader";
import IconSpinner from "components/IconSpinner";
import Button from "components/Button";
import SimpleModal from "components/SimpleModal";
import IconTableHeader from "components/IconTableHeader";
import IconTable from "components/IconTable";
import type { IconTableColumn } from "components/IconTable";
import type { RootState } from "types/state";
// Both modals are TS with precise props (memberId/fieldType: string|FieldType,
// client: required ApolloClient). This page's call sites pass looser values —
// editMember.entityId is string|null, editFieldType is string|null, and client
// is optional from withApollo — matching the original JS render. Cast the
// imports to a loose component type so those values pass through unchanged.
import MemberFieldEditModalTyped from "./MemberFieldEditModal";
import BusinessFieldEditModalTyped from "./BusinessFieldEditModal";

const MemberFieldEditModal =
  MemberFieldEditModalTyped as unknown as React.ComponentType<
    Record<string, unknown>
  >;
const BusinessFieldEditModal =
  BusinessFieldEditModalTyped as unknown as React.ComponentType<
    Record<string, unknown>
  >;

/**
 * A KYB business member as returned by getCompanyKybData — only the fields this
 * page reads are modeled; memberProfile is intentionally loose because the
 * runtime reads several optional profile fields via lodash `get`.
 */
interface BusinessMember {
  id: string;
  memberProfile: {
    firstName?: string;
    lastName?: string;
    ssn?: string;
    dob?: string;
    address1?: string;
    address2?: string;
    city?: string;
    state?: string;
    postalCode?: string;
  };
}

/** A KYB document row as read by this page (loosely shaped, see usage). */
interface KybDocument {
  id: string;
  name: string;
  contentType: string;
  createdAt: string;
  type?: string;
  memberId?: string;
  isBusinessDocument?: boolean;
  metadata?: Record<string, unknown>;
}

/** A document staged for upload (from redux userDocuments slice). */
interface UploadDocument {
  docUrl: string;
  name: string;
  contentType: string;
  docType: string;
  documentFace?: string;
  metadata: {
    businessMemberId?: string;
    companyId?: string;
  };
}

/** A document type option as returned by getKybDocumentTypes. */
interface DocumentTypeOption {
  id: string;
  value: string;
}

/** A verification-history entry carrying tags used to derive editable fields. */
type VerificationHistory = Array<{ tags?: string[] }> | undefined;

/** A KYC response row from getKybStatusForCompany. */
interface KycResponse {
  reference: string;
  message: string;
  entityType: string;
  status: string;
  verificationHistory?: Array<{ tags?: string[] }>;
}

interface EmployerProfile {
  businessName?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  ein?: string;
}

/** A verification detail row rendered in the metadata modal. */
interface VerificationDetail {
  verificationId: string;
  verificationStatus: string;
  kycLevel: string;
  tags: string[];
  reasons: string[];
}

/** A formatted status-table row built by _getStatusData. */
interface StatusRow {
  referance: string;
  entityName: string;
  entityType: string;
  status: string;
  message: string;
  verificationHistory?: Array<{ tags?: string[] }>;
  entityId: string | null;
  isBusiness: boolean;
}

const BUSINESS_DOCS = [
  {
    value: "irs_einverification",
    label: "IRS EIN Verification",
  },
  {
    value: "articlesofincorporation",
    label: "Articles of Incorporation",
  },
  {
    value: "irs_ss4",
    label: "IRS SS-4",
  },
  {
    value: "certificateofformation",
    label: "Certificate of Formation",
  },
  {
    value: "certificateofgoodstanding",
    label: "Certificate of Good Standing",
  },
  {
    value: "businesslicense",
    label: "Business License",
  },
  {
    value: "bankstatement",
    label: "Bank Statement",
  },
  {
    value: "leaseagreement",
    label: "Lease Agreement",
  },
  {
    value: "utilitybill",
    label: "Utility Bill",
  },
];

const isSubmittingSelector = createLoadingSelector([
  adminConstants.SUBMIT_BUSINESS_KYB_DOCUMENTS,
  adminConstants.SUBMIT_BUSINESS_MEMBERS_KYB_DOCUMENTS,
]);

const isSubmittingBusinessRegistrationSelector = createLoadingSelector([
  adminConstants.REQUEST_BUSINESS_REGISTRATION_UPDATE,
]);

const errorSelector = createErrorSelector([
  adminConstants.SUBMIT_BUSINESS_KYB_DOCUMENTS,
  adminConstants.SUBMIT_BUSINESS_MEMBERS_KYB_DOCUMENTS,
  adminConstants.GET_KYB_STATUS,
]);

const businessRegistrationErrorSelector = createErrorSelector(
  adminConstants.REQUEST_BUSINESS_REGISTRATION_UPDATE
);

const mapStateToProps = (state: RootState) => {
  // userDocuments slice documents carry extra metadata fields (businessMemberId,
  // companyId) not on the typed Document model — cast at the boundary.
  const documents = state.userDocuments
    .documents as unknown as UploadDocument[];
  const memberDocuments = filter(documents, (document) => {
    return !!document.metadata && !!document.metadata.businessMemberId;
  });
  const businessDocuments = filter(documents, (document) => {
    return !!document.metadata && !!document.metadata.companyId;
  });
  return {
    allMemberDocuments: memberDocuments,
    allBusinessDocuments: businessDocuments,
    isSubmitting: isSubmittingSelector(state),
    isSubmittingBusinessRegistration:
      isSubmittingBusinessRegistrationSelector(state),
    error: errorSelector(state),
    businessRegistrationError: businessRegistrationErrorSelector(state),
  };
};

const mapDispatchToProps = {
  submitBusinessDocuments,
  submitBusinessMemberDocuments,
  submitBusinessRegistrationUpdate,
  getKybStatusAndInfo,
  clearUploadedDocuments,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  companyId: string;
}

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

interface State {
  selectedDocTypeByMemberId: Record<string, string>;
  kybStatus: object;
  selectedMember: string;
  initialFetching: boolean;
  businessMembers: BusinessMember[];
  businessMemberDocuments: KybDocument[];
  // written by _getKybMemberData (not in the initial state object)
  businessDocuments?: KybDocument[];
  documentTypes: DocumentTypeOption[];
  isFetching: boolean;
  isMounted: boolean;
  showMetadataModal: boolean;
  verificationDetails: VerificationDetail[];
  selectedBusinessDocType: string;
  showEditModal: boolean;
  editMember: StatusRow | null;
  editFieldType: string | null;
  isSubmittingEdit: boolean;
  showBusinessFieldModal: boolean;
  businessFieldType: string | null;
  // written by _getKybMemberData (not in the initial state object)
  employerProfile?: EmployerProfile;
  // written by _getKybMemberData (not in the initial state object)
  kycResponses?: KycResponse[];
}

class MemberDocs extends PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      selectedDocTypeByMemberId: {},
      kybStatus: {},
      selectedMember: "",
      initialFetching: true,
      businessMembers: [],
      businessMemberDocuments: [],
      documentTypes: [],
      isFetching: false,
      isMounted: false,
      showMetadataModal: false,
      verificationDetails: [],
      selectedBusinessDocType: "",
      showEditModal: false,
      editMember: null,
      editFieldType: null,
      isSubmittingEdit: false,
      showBusinessFieldModal: false,
      businessFieldType: null,
    };
  }

  async componentDidMount() {
    this.setState({ isMounted: true });
    await this._getKybMemberData();
    this.setState({ initialFetching: false });
  }

  async componentWillUnmount() {
    this.setState({
      isMounted: false,
    });
  }

  async _getKybMemberData() {
    this.setState({ isFetching: true });
    // The GraphQL response carries getCompanyKybData and the page also reads a
    // getKybStatusForCompany field off `data`; both are loosely shaped here, so
    // cast the awaited result to the shapes this method consumes.
    const {
      data: { getCompanyKybData: kybData, getKybStatusForCompany: kybStatus },
    } = (await this.props.getKybStatusAndInfo(
      this.props.client as ApolloClient<object>,
      this.props.companyId
    )) as unknown as {
      data: {
        getCompanyKybData: {
          businessMembers: BusinessMember[];
          businessMemberDocuments: KybDocument[];
          businessDocuments: KybDocument[];
          employerProfile: EmployerProfile;
        };
        getKybStatusForCompany: { kycResponses: KycResponse[] };
      };
    };

    const {
      businessMembers,
      businessMemberDocuments,
      businessDocuments,
      employerProfile,
    } = kybData;

    // getKybDocumentTypes returns Maybe<Array<Maybe<{id, value}>>>; at runtime
    // the entries are always present {id, value} rows — narrow at the boundary.
    let documentTypes = (await getKybDocumentTypes(
      this.props.client as ApolloClient<object>,
      this.props.companyId
    )) as DocumentTypeOption[];
    documentTypes = documentTypes.filter((doc) =>
      [
        "tax_1040",
        "vtl_birth_certificate",
        "id_drivers_permit",
        "tax_w2",
        "doc_lease",
        "id_passport",
        "doc_ss_card",
        "id_state",
        "id_drivers_license",
        "tax_1099",
        "id_passport_card",
        "doc_utility",
        "doc_bank_statement",
        "doc_business_license",
        "doc_cert_of_formation",
        "doc_cert_of_good_standing",
        "doc_tax_ss4",
        "tax_W4",
      ].includes(doc.id)
    );
    const formattedBusinessDocuments = chain(businessDocuments)
      .filter((doc) => {
        return some(BUSINESS_DOCS, ({ value }) => {
          return toLower(value) === toLower(doc.type);
        });
      })
      .map((doc) => {
        return {
          ...doc,
          isBusinessDocument: true,
        };
      })
      .value();

    this.setState({
      businessMembers,
      businessMemberDocuments,
      businessDocuments: formattedBusinessDocuments,
      documentTypes,
      employerProfile: {
        ...employerProfile,
      },
      kycResponses: kybStatus.kycResponses,
      isFetching: false,
    });
  }

  _submitDocuments = async () => {
    const hasMemberDocs = !isEmpty(this._getMemberDocuments());
    const hasBusinessDocs = !isEmpty(this._getBusinessDocuments());
    let succeeded = true;
    if (hasMemberDocs) {
      const formattedDocuments = map(
        this._getMemberDocuments(),
        ({
          docUrl: url,
          name,
          contentType,
          docType: type,
          documentFace,
          metadata: { businessMemberId },
        }) => {
          return {
            url,
            name,
            contentType,
            type,
            metadata: {
              employerBusinessMemberId: businessMemberId,
              documentFace,
            },
          };
        }
      );

      const memberResult = await this.props.submitBusinessMemberDocuments(
        this.props.client as ApolloClient<object>,
        {
          companyId: this.props.companyId,
          documents: formattedDocuments,
        } as Parameters<typeof submitBusinessMemberDocuments>[1]
      );
      succeeded = memberResult.success;
    }

    if (hasBusinessDocs) {
      const formattedDocuments = map(
        this._getBusinessDocuments(),
        ({ docUrl: url, name, contentType, docType: type }) => {
          return {
            url,
            name,
            contentType,
            type,
            metadata: {},
          };
        }
      );

      const businessResult = await this.props.submitBusinessDocuments(
        this.props.client as ApolloClient<object>,
        {
          companyId: this.props.companyId,
          documents: formattedDocuments,
        } as Parameters<typeof submitBusinessDocuments>[1]
      );
      succeeded = succeeded && businessResult.success;
    }

    if (succeeded) {
      toast.success("Successfully uploaded documents.");
      this.props.clearUploadedDocuments();

      setTimeout(() => {
        if (this.state.isMounted) {
          void this._getKybMemberData();
        }
      }, 5000);
    } else {
      toast.error("Something went wrong with uploading documents.");
    }
  };

  // Status Tables
  _getStatusData = (): Array<StatusRow | ""> => {
    // kycResponses is only populated after _getKybMemberData resolves; render
    // guards on initialFetching, so the original code reads it unconditionally.
    return this.state.kycResponses!.map((response) => {
      let entityId: string | null = null;
      const entityInfo = response.message.split(" ")[0];
      const isEmployerMessage = entityInfo.startsWith("Employer:");
      if (entityInfo.includes(":")) {
        entityId = entityInfo.split(":")[1];
      } else {
        entityId = null;
      }
      // Business can be identified by entityType or by "Employer:" prefix in message
      const isBusiness =
        response.entityType === "business" || isEmployerMessage;

      let entityName: string;
      if (isBusiness) {
        entityName = get(
          this.state,
          "employerProfile.businessName",
          "Business"
        );
      } else if (!entityId) {
        return "";
      } else {
        const matchingMember = find(this.state.businessMembers, {
          id: entityId,
        });
        const { firstName, lastName } = get(
          matchingMember,
          "memberProfile",
          {}
        ) as BusinessMember["memberProfile"];
        entityName = `${firstName || ""} ${lastName || ""}`.trim();
      }

      return {
        referance: response.reference,
        entityName,
        entityType: isBusiness ? "business" : response.entityType,
        status: response.status,
        message: response.message,
        verificationHistory: response.verificationHistory,
        entityId,
        isBusiness,
      };
    });
  };

  _getEditableField = (verificationHistory: VerificationHistory) => {
    const allTags = chain(verificationHistory)
      .flatMap((v) => get(v, "tags", []))
      .value();

    const hasSsnMiskey = allTags.includes("SSN Miskey");
    const hasDobMiskey = allTags.includes("DOB Miskey");
    const hasAddressIssue =
      allTags.includes("Address Warning") ||
      allTags.includes("CIP Address Not Verified");

    const categoryCount = [hasSsnMiskey, hasDobMiskey, hasAddressIssue].filter(
      Boolean
    ).length;

    if (categoryCount !== 1) return null;

    if (hasSsnMiskey) return "ssn";
    if (hasDobMiskey) return "dob";
    if (hasAddressIssue) return "address";
    return null;
  };

  _openEditModal = (member: StatusRow, fieldType: string) => {
    this.setState({
      showEditModal: true,
      editMember: member,
      editFieldType: fieldType,
    });
  };

  _closeEditModal = () => {
    this.setState({
      showEditModal: false,
      editMember: null,
      editFieldType: null,
    });
  };

  _handleFieldEditSubmit = async (data: {
    memberId: string;
    fieldType: string;
    address?: unknown;
    value?: unknown;
  }) => {
    this.setState({ isSubmittingEdit: true });

    try {
      const params: {
        companyId: string;
        memberId: string;
        fieldType: string;
        address?: unknown;
        value?: unknown;
      } = {
        companyId: this.props.companyId,
        memberId: data.memberId,
        fieldType: data.fieldType,
      };

      if (data.fieldType === "address") {
        params.address = data.address;
      } else {
        params.value = data.value;
      }

      await requestMemberFieldEdit(
        this.props.client as ApolloClient<object>,
        params as Parameters<typeof requestMemberFieldEdit>[1]
      );

      toast.success("Successfully submitted for approval.");
      this._closeEditModal();
    } catch (error) {
      toast.error("Something went wrong submitting the edit request.");
    } finally {
      this.setState({ isSubmittingEdit: false });
    }
  };

  _getBusinessEditableFields = (verificationHistory: VerificationHistory) => {
    const allTags = chain(verificationHistory)
      .flatMap((v) => get(v, "tags", []))
      .value();

    const fields: string[] = [];
    if (allTags.includes("Business Address Unmatched")) fields.push("address");
    if (allTags.includes("FEIN Unmatched")) fields.push("ein");
    if (allTags.includes("Business Name Unmatched"))
      fields.push("businessName");
    return fields;
  };

  _openBusinessFieldModal = (fieldType: string) => {
    this.setState({
      showBusinessFieldModal: true,
      businessFieldType: fieldType,
    });
  };

  _closeBusinessFieldModal = () => {
    this.setState({ showBusinessFieldModal: false, businessFieldType: null });
  };

  _handleBusinessFieldSubmit = async (data: Record<string, unknown>) => {
    const result = (await this.props.submitBusinessRegistrationUpdate(
      this.props.client as ApolloClient<object>,
      {
        companyId: this.props.companyId,
        ...data,
      } as Parameters<typeof submitBusinessRegistrationUpdate>[1]
    )) as { success: boolean; error?: string };

    if (!result.success) {
      toast.error(result.error);
    } else {
      toast.success("Successfully submitted for approval.");
      this._closeBusinessFieldModal();
    }
  };

  _buildStatusColumns = (): Array<IconTableColumn<StatusRow>> => {
    const columns: Array<IconTableColumn<StatusRow>> = [
      {
        label: "Entity ID",
        key: "entityId",
      },
      {
        label: "Entity Name",
        key: "entityName",
      },
      {
        label: "Entity Type",
        key: "entityType",
      },
      {
        label: "Status",
        key: "status",
      },
      {
        label: "Tags",
        customComponent: (props) => {
          const allTags = chain(props.verificationHistory)
            .flatMap((v) => get(v, "tags", []))
            .value();
          if (isEmpty(allTags)) {
            return <div>—</div>;
          }
          return (
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {map(allTags, (tag, idx) => (
                <li key={idx}>{tag}</li>
              ))}
            </ul>
          );
        },
      },
      {
        label: "Actions",
        customComponent: (props) => {
          if (props.isBusiness) {
            const editableFields = this._getBusinessEditableFields(
              props.verificationHistory
            );
            if (!editableFields.length) {
              return <div>—</div>;
            }
            const fieldLabels: Record<string, string> = {
              address: "Address",
              ein: "EIN",
              businessName: "Business Name",
            };
            return (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {editableFields.map((field) => (
                  // @ts-expect-error Button `name` is required in ButtonProps but
                  // optional in practice (spread to <button name={undefined}>);
                  // original JS omitted it — pre-existing inert prop omission.
                  <Button
                    key={field}
                    btnLabel={`Edit ${fieldLabels[field]}`}
                    size="sm"
                    color="action"
                    onClick={() => this._openBusinessFieldModal(field)}
                  />
                ))}
              </div>
            );
          }

          const editableField = this._getEditableField(
            props.verificationHistory
          );
          if (!editableField) {
            return <div>—</div>;
          }
          const fieldLabels: Record<string, string> = {
            ssn: "SSN",
            dob: "DOB",
            address: "Address",
          };
          return (
            // @ts-expect-error Button `name` is required in ButtonProps but
            // optional in practice (spread to <button name={undefined}>);
            // original JS omitted it — pre-existing inert prop omission.
            <Button
              btnLabel={`Edit ${fieldLabels[editableField]}`}
              size="sm"
              color="action"
              onClick={() => this._openEditModal(props, editableField)}
            />
          );
        },
      },
    ];
    return columns;
  };

  _buildStatusSection = () => {
    const columns = this._buildStatusColumns();
    // _getStatusData can emit "" rows for entity-less responses (pre-existing);
    // IconTable iterates them harmlessly — cast to the row type at the boundary.
    const data = this._getStatusData() as StatusRow[];
    return (
      <>
        <IconTableHeader tableHeader="Status" />
        <IconTable columns={columns} data={data} />
      </>
    );
  };

  _buildMembersSection = () => {
    return map(this.state.businessMembers, (member) => {
      const { firstName, lastName } = member.memberProfile;
      return this._buildUploaderSection({
        entityName: `${firstName} ${lastName}`,
        entityId: member.id,
        entityHandle: member.id,
      });
    });
  };

  _buildBusinessUploadSection = () => {
    return this._buildUploaderSection({
      // employerProfile is populated by _getKybMemberData before render passes
      // the initialFetching guard; original reads it unconditionally.
      entityName: this.state.employerProfile!.businessName,
      entityId: this.props.companyId,
      entityHandle: "",
      isCompany: true,
    });
  };

  _buildUploaderSection({
    entityName,
    entityHandle,
    entityId,
    isCompany,
  }: {
    entityName?: string;
    entityHandle: string;
    entityId: string;
    isCompany?: boolean;
  }) {
    const showUploader = isCompany
      ? !!this.state.selectedBusinessDocType
      : !!this.state.selectedDocTypeByMemberId[entityId];
    const showBackUploader =
      !isCompany &&
      ["id_drivers_license", "id_state"].includes(
        this.state.selectedDocTypeByMemberId[entityId]
      );
    const docType = isCompany
      ? this.state.selectedBusinessDocType
      : this.state.selectedDocTypeByMemberId[entityId];

    const fileMetadata = isCompany
      ? { companyId: entityId }
      : { businessMemberId: entityId };

    const successMessage =
      "Your file was successfully uploaded. You can now add more files.";

    return (
      <div style={{ marginBottom: 16 }}>
        <h5 style={{ marginBottom: 4 }}>
          Upload docs for {entityName} {!isCompany && `- ${entityHandle}`}
        </h5>

        <article className="col-form" style={{ paddingBottom: 0 }}>
          {isCompany ? (
            <Form.Group
              as={Col}
              sm={6}
              controlId="formBasicSize"
              style={{ paddingLeft: 0 }}
            >
              <Form.Select
                name="documentType"
                defaultValue={""}
                value={this.state.selectedBusinessDocType}
                onChange={(e) =>
                  this.setState({
                    selectedBusinessDocType: e.target.value,
                  })
                }
              >
                <option value="">Select Document Type</option>
                {map(BUSINESS_DOCS, (doc) => {
                  return <option value={doc.value}>{doc.label}</option>;
                })}
              </Form.Select>
            </Form.Group>
          ) : (
            <Form.Group
              as={Col}
              sm={6}
              controlId="formBasicSize"
              style={{ paddingLeft: 0 }}
            >
              <Form.Select
                name="documentType"
                defaultValue={""}
                value={this.state.selectedDocTypeByMemberId[entityId]}
                onChange={(e) =>
                  this.setState({
                    selectedDocTypeByMemberId: {
                      ...this.state.selectedDocTypeByMemberId,
                      [entityId]: e.target.value,
                    },
                  })
                }
              >
                <option value="">Select Document Type</option>
                {map(this.state.documentTypes, (doc) => {
                  return <option value={doc.id}>{doc.value}</option>;
                })}
              </Form.Select>
            </Form.Group>
          )}
          {showUploader && (
            <Row className="mb-3">
              <Form.Group as={Col} sm={6}>
                <Form.Label>Front of Document:</Form.Label>
                <FileUploader
                  fileLimit={8}
                  docType={docType}
                  metadata={fileMetadata}
                  fileUploadedMsg={successMessage}
                  documentFace="FRONT"
                />
              </Form.Group>
              {showBackUploader && (
                <Form.Group as={Col} sm={6}>
                  <Form.Label>Back of Document:</Form.Label>
                  <FileUploader
                    fileLimit={1}
                    docType={docType}
                    metadata={fileMetadata}
                    fileUploadedMsg={successMessage}
                    documentFace="BACK"
                  />
                </Form.Group>
              )}
            </Row>
          )}
        </article>
      </div>
    );
  }

  _calcNotEnoughDocs = () => {
    const noDocs =
      isEmpty(this._getMemberDocuments()) &&
      isEmpty(this._getBusinessDocuments());

    return noDocs;
  };

  //Member Docs Table
  _getMemeberDocsData = () => {
    return map(
      // businessDocuments is set by _getKybMemberData before this runs; the
      // original spreads it unconditionally.
      [...this.state.businessMemberDocuments, ...this.state.businessDocuments!],
      ({ name, createdAt, contentType, memberId, id, isBusinessDocument }) => {
        const uploadDate = moment(createdAt).format("YYYY-MM-DD");
        let entityDetails: string;

        if (isBusinessDocument) {
          entityDetails = `${this.state.employerProfile!.businessName}`;
        } else {
          // found-not-fixed: find() can return undefined (no matching member),
          // but member.memberProfile/member.id are read unconditionally — would
          // throw at runtime; preserved verbatim.
          const member = find(this.state.businessMembers, { id: memberId })!;
          const { firstName, lastName } = member.memberProfile;
          entityDetails = `${firstName} ${lastName} - ${member.id}`;
        }

        return {
          id,
          name,
          contentType,
          uploadDate,
          entityDetails,
        };
      }
    );
  };

  _buildMemberDocsColumns = () => {
    const columns = [
      {
        label: "Document Type",
        key: "name",
      },

      {
        label: "File Type",
        key: "contentType",
      },
      {
        label: "Upload Date",
        key: "uploadDate",
      },
      {
        label: "Member",
        key: "entityDetails",
      },
    ];
    return columns;
  };

  _getMemberDocsTable = () => {
    const columns = this._buildMemberDocsColumns();
    const data = this._getMemeberDocsData();

    const noDocs =
      isEmpty(this.state.businessMemberDocuments) &&
      isEmpty(this.state.businessDocuments);
    if (noDocs) {
      return null;
    }

    return (
      <div className="business-member-documents">
        <IconTableHeader
          tableHeader="Submitted Documents"
          tableCount={size(data)}
        />
        <IconTable columns={columns} data={data} />
      </div>
    );
  };

  //Verification Table
  _getVerificationData = () => {
    return this.state.verificationDetails.map((verification) => {
      const tags = verification.tags.join(", ");
      const reasons = verification.reasons.join(", ");
      return {
        id: verification.verificationId,
        status: verification.verificationStatus,
        kyc: verification.kycLevel,
        tags,
        reasons,
      };
    });
  };

  _buildVerificationColumns = () => {
    const columns = [
      {
        label: "Verification Status",
        key: "status",
      },

      {
        label: "KYC Level",
        key: "kyc",
      },
      {
        label: "Tags",
        key: "tags",
      },
      {
        label: "Reasons",
        key: "reasons",
      },
    ];
    return columns;
  };

  _buildVerificationDetailsTable = () => {
    const columns = this._buildVerificationColumns();
    const data = this._getVerificationData();

    return (
      <>
        <IconTableHeader
          tableHeader="Verification Details"
          tableCount={size(data)}
        />
        <IconTable columns={columns} data={data} />
      </>
    );
  };

  _getMemberDocuments = () => {
    return filter(this.props.allMemberDocuments, (document) => {
      return get(this.state, "businessMembers", [] as BusinessMember[])
        .map((m) => m.id)
        .includes(document.metadata.businessMemberId as string);
    });
  };

  _getBusinessDocuments = () => {
    return filter(this.props.allBusinessDocuments, (document) => {
      return this.props.companyId === document.metadata.companyId;
    });
  };

  render() {
    if (this.state.initialFetching) {
      return <IconSpinner centered />;
    }

    const notEnoughDocuments = this._calcNotEnoughDocs();

    return (
      <div className="member-docs">
        {this.state.isFetching && <IconSpinner centered />}
        {this.state.showMetadataModal && (
          <SimpleModal
            show={true}
            size={"lg"}
            onClose={() =>
              this.setState({
                showMetadataModal: false,
                // found-not-fixed: verificationDetails is an array everywhere
                // else but is reset to {} here; preserved verbatim.
                // @ts-expect-error assigning {} to a VerificationDetail[] field
                verificationDetails: {},
              })
            }
            title="Verification Details"
            buttonText="close"
          >
            {this._buildVerificationDetailsTable()}
          </SimpleModal>
        )}

        {this.state.showEditModal && this.state.editMember && (
          <MemberFieldEditModal
            show={this.state.showEditModal}
            onClose={this._closeEditModal}
            onSubmit={this._handleFieldEditSubmit}
            memberName={this.state.editMember.entityName}
            memberId={this.state.editMember.entityId}
            fieldType={this.state.editFieldType}
            client={this.props.client}
            currentSsn={get(
              find(this.state.businessMembers, {
                id: this.state.editMember.entityId,
              }),
              "memberProfile.ssn"
            )}
            currentDob={get(
              find(this.state.businessMembers, {
                id: this.state.editMember.entityId,
              }),
              "memberProfile.dob"
            )}
            currentAddress={{
              address1: get(
                find(this.state.businessMembers, {
                  id: this.state.editMember.entityId,
                }),
                "memberProfile.address1",
                ""
              ),
              address2: get(
                find(this.state.businessMembers, {
                  id: this.state.editMember.entityId,
                }),
                "memberProfile.address2",
                ""
              ),
              city: get(
                find(this.state.businessMembers, {
                  id: this.state.editMember.entityId,
                }),
                "memberProfile.city",
                ""
              ),
              state: get(
                find(this.state.businessMembers, {
                  id: this.state.editMember.entityId,
                }),
                "memberProfile.state",
                ""
              ),
              postalCode: get(
                find(this.state.businessMembers, {
                  id: this.state.editMember.entityId,
                }),
                "memberProfile.postalCode",
                ""
              ),
            }}
            isSubmitting={this.state.isSubmittingEdit}
          />
        )}

        {this.state.showBusinessFieldModal && (
          <BusinessFieldEditModal
            show={this.state.showBusinessFieldModal}
            onClose={this._closeBusinessFieldModal}
            onSubmit={this._handleBusinessFieldSubmit}
            businessName={get(this.state, "employerProfile.businessName", "")}
            client={this.props.client}
            fieldType={this.state.businessFieldType}
            currentAddress={{
              address1: get(this.state, "employerProfile.address1", ""),
              address2: get(this.state, "employerProfile.address2", ""),
              city: get(this.state, "employerProfile.city", ""),
              state: get(this.state, "employerProfile.state", ""),
              postalCode: get(this.state, "employerProfile.postalCode", ""),
            }}
            currentEin={get(this.state, "employerProfile.ein", "")}
            currentBusinessName={get(
              this.state,
              "employerProfile.businessName",
              ""
            )}
            isSubmitting={this.props.isSubmittingBusinessRegistration}
          />
        )}

        <h3>KYB Details:</h3>
        {this._buildStatusSection()}
        <h3>Upload Member Documents:</h3>
        {this._buildMembersSection()}
        <h3 style={{ marginTop: 8 }}>Upload Business Documents:</h3>
        {this._buildBusinessUploadSection()}
        <Button
          disabled={notEnoughDocuments}
          onClick={this._submitDocuments}
          withArrow
          name="submit"
          btnLabel={"Submit Documents"}
          loading={this.props.isSubmitting}
        />
        {this._getMemberDocsTable()}
      </div>
    );
  }
}

export default connector(withApollo<Props>(MemberDocs));
